import { useCallback, useMemo, useState } from 'react';

import { GridSortModel } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';

import { queryParameters } from '~constants';

const useUrlSearchParamsSorting = (
	initialSortModel?: GridSortModel,
	disableUrlSync = false
): [GridSortModel | undefined, (model: GridSortModel) => void] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const sortingParam = searchParams.get(queryParameters.SortBy);
	const orderParam = searchParams.get(queryParameters.OrderBy);
	const urlSortModel = useMemo<GridSortModel | undefined>(
		() =>
			sortingParam != null ?
				[
					{
						field: sortingParam,
						sort: orderParam === 'desc' ? 'desc' : 'asc',
					},
				]
			:	initialSortModel,
		[sortingParam, orderParam, initialSortModel]
	);

	const [internalSortModel, setInternalSortModel] = useState<GridSortModel | undefined>(
		initialSortModel
	);

	const setSortModel = useCallback(
		(model: GridSortModel) => {
			if (disableUrlSync) {
				setInternalSortModel(model);
				return;
			}

			setSearchParams(
				(prev) => {
					if (!model[0]) {
						prev.delete(queryParameters.SortBy);
						prev.delete(queryParameters.OrderBy);
					} else {
						prev.set(queryParameters.SortBy, model[0].field);

						if (model[0].sort === 'desc' || model[0].sort === 'asc') {
							prev.set(queryParameters.OrderBy, model[0].sort);
						} else {
							prev.delete(queryParameters.OrderBy);
						}
					}
					return prev;
				},
				{ replace: true }
			);
		},
		[disableUrlSync, setSearchParams]
	);

	return [!disableUrlSync ? urlSortModel : internalSortModel, setSortModel];
};

export default useUrlSearchParamsSorting;
